import { ActionIcon, Group, Menu, MenuItemProps, Paper, Stack, Text, Title, UnstyledButton } from "@mantine/core";
import { IconDots } from "@tabler/icons-react";
import { ReactNode } from "@tanstack/react-router";
import { MessageStatusBadge } from "shared/components/message/MessageStatusBadge";
import { getRelativeTime } from "shared/components/RelativeTime";
import { titlecase } from "shared/utils/fns";
import { MessageSeverity, MessageStatus } from "src/gql/graphql";

export type MessageDetailCardAction = {
    icon: ReactNode;
    label: string;
    onClick: (e: React.MouseEvent) => unknown;
    disabled?: boolean;
} & Omit<MenuItemProps, "onClick">;

type MessageDetailCardProps = {
    title?: string;
    unread?: boolean;
    timestamp?: Date | string;
    severity?: MessageSeverity;
    body?: string;
    status?: MessageStatus;
    actions?: MessageDetailCardAction[];
    onClick?: () => unknown;
};

const MessageDateline = ({ severity, timestamp }: { timestamp?: Date | string; severity?: MessageSeverity }) => {
    const ts = `${!!timestamp ? getRelativeTime(typeof timestamp == "string" ? new Date(+timestamp) : timestamp) : ""}`;
    if (severity == MessageSeverity.Urgent && !!timestamp) return `${titlecase(severity.toLowerCase())} | ${ts}`;
    else return ts;
};

export const MessageDetailCard = ({
    title = "",
    unread,
    timestamp,
    status,
    severity = MessageSeverity.Informational,
    body = "",
    actions,
    ...rest
}: MessageDetailCardProps) => {
    return (
        <UnstyledButton w="100%" {...rest}>
            <Paper shadow="md" radius="md" px="sm" py="md" w="100%">
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Title order={3} c={!unread ? "dimmed" : undefined}>
                        {title}
                    </Title>
                    <Group wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                        {status && <MessageStatusBadge status={status} />}
                        {!!actions?.length && (
                            <Menu>
                                <Menu.Target>
                                    <ActionIcon
                                        variant="transparent"
                                        onClick={(e) => e.stopPropagation()}
                                        data-prevent-link
                                        aria-label="Show message options"
                                    >
                                        <IconDots size={16} />
                                    </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {actions.map(({ label, icon, onClick, disabled, ...rest }) => (
                                        <Menu.Item
                                            disabled={disabled}
                                            key={label}
                                            leftSection={icon}
                                            rightSection={
                                                <Text size="sm" c="inherit">
                                                    {label}
                                                </Text>
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onClick(e);
                                            }}
                                            data-prevent-link
                                            {...rest}
                                        />
                                    ))}
                                </Menu.Dropdown>
                            </Menu>
                        )}
                    </Group>
                </Group>

                <Stack gap={"xs"}>
                    <Text size="sm" c={!unread ? "dimmed" : undefined}>
                        <MessageDateline severity={severity} timestamp={timestamp} />
                    </Text>
                    <Text lineClamp={4} c={!unread ? "dimmed" : undefined}>
                        {body}
                    </Text>
                </Stack>
            </Paper>
        </UnstyledButton>
    );
};
